/**
 * Frontend validation utilities.
 */

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Minimum password length requirement.
 */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * Validates a password string.
 *
 * @param password - The password to validate
 * @returns Validation result with isValid flag and optional error message
 */
export function validatePassword(password: string): ValidationResult {
  if (!password) {
    return { isValid: false, error: 'Senha é obrigatória' };
  }

  if (password.length < MIN_PASSWORD_LENGTH) {
    return {
      isValid: false,
      error: `Senha deve ter no mínimo ${MIN_PASSWORD_LENGTH} caracteres`,
    };
  }

  // Check for at least one letter and one number
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /\d/.test(password);

  if (!hasLetter || !hasNumber) {
    return {
      isValid: false,
      error: 'Senha deve conter letras e números',
    };
  }

  return { isValid: true };
}

/**
 * Validates that two passwords match.
 *
 * @param password - The password
 * @param confirmPassword - The confirmation password
 * @returns Validation result
 */
export function validatePasswordMatch(
  password: string,
  confirmPassword: string
): ValidationResult {
  if (password !== confirmPassword) {
    return { isValid: false, error: 'As senhas não coincidem' };
  }
  return { isValid: true };
}

/**
 * Validates an email address format.
 *
 * @param email - The email to validate
 * @returns Validation result
 */
export function validateEmail(email: string): ValidationResult {
  if (!email) {
    return { isValid: false, error: 'Email é obrigatório' };
  }

  // Basic email regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Formato de email inválido' };
  }

  return { isValid: true };
}

/**
 * Validates a name string.
 *
 * @param name - The name to validate
 * @param minLength - Minimum length (default: 2)
 * @returns Validation result
 */
export function validateName(name: string, minLength = 2): ValidationResult {
  if (!name) {
    return { isValid: false, error: 'Nome é obrigatório' };
  }

  if (name.trim().length < minLength) {
    return {
      isValid: false,
      error: `Nome deve ter no mínimo ${minLength} caracteres`,
    };
  }

  return { isValid: true };
}

/**
 * Validates a room code.
 *
 * @param code - The room code to validate
 * @returns Validation result
 */
export function validateRoomCode(code: string): ValidationResult {
  if (!code) {
    return { isValid: false, error: 'Código da sala é obrigatório' };
  }

  if (code.length !== 6) {
    return { isValid: false, error: 'Código da sala deve ter 6 caracteres' };
  }

  // Only alphanumeric
  if (!/^[A-Za-z0-9]+$/.test(code)) {
    return { isValid: false, error: 'Código deve conter apenas letras e números' };
  }

  return { isValid: true };
}
